import { createSlice } from "@reduxjs/toolkit";

import {
  addNewDocumentData,
  addDocumentTypeThunk,
  deleteDocument,
  deleteDocumentTypeThunk,
  fetchDocumentList,
  fetchDocumentTypeList,
  updateDocument,
  updateDocumentTypeThunk,
} from "../thunks/documents";

import type { Document, DocumentType } from "../../utils/types/document";

interface DocumentListState {
  documentList: {
    [key: string]: {
      data: Document[];
    };
  };
  documentTypeList: DocumentType[];
  documentTypeLoading: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: DocumentListState = {
  documentList: {},
  documentTypeList: [],
  documentTypeLoading: false,
  loading: false,
  error: null,
};

export const documentListSlice = createSlice({
  name: "document",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentList.fulfilled, (state, action) => {
        // Initialize the entry if it doesn't exist
        if (!state.documentList[action.payload.id]) {
          state.documentList[action.payload.id] = { data: []};
        }
        state.documentList[action.payload.id].data = action.payload.data.data;
        // state.documentList[action.payload.id].total = action.payload.data.total;
        state.loading = false;
      })
      .addCase(fetchDocumentList.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });

    builder
      .addCase(fetchDocumentTypeList.fulfilled, (state, action) => {
        state.documentTypeList = action.payload || [];
        // state.loading = false;
      })
      .addCase(fetchDocumentTypeList.rejected, (state, action) => {
        state.error = action.payload as string;
        // state.loading = false;
      });

    builder
      .addCase(updateDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDocument.fulfilled, (state, action) => {
        state.loading = false;
        // Check if the entry exists before trying to update it
        if (state.documentList[action.payload.id]) {
          state.documentList[action.payload?.id].data = state.documentList[
            action.payload?.id
          ].data.map((document) => {
            if (document.documentId === action.payload?.data?.documentId) {
              return { ...document, ...action.payload?.data };
            }
            return document;
          });
        }
      })
      .addCase(updateDocument.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(deleteDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.loading = false;
        // Check if the entry exists before trying to filter and update it
        if (state.documentList[action.payload?.jobId]) {
          state.documentList[action.payload.jobId].data = state.documentList[
            action.payload.jobId
          ].data.filter(
            (document) => document.documentId !== action.payload?.id
          );
          // state.documentList[action.payload.jobId].total -= 1;
        }
      })
      .addCase(deleteDocument.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(addNewDocumentData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewDocumentData.fulfilled, (state, action) => {
        state.loading = false;
        // Initialize the entry if it doesn't exist
        if (!state.documentList[action.payload.jobId]) {
          state.documentList[action.payload.jobId] = { data: []};
        }
        state.documentList[action.payload.jobId].data = [
          action.payload.data,
          ...state.documentList[action.payload.jobId].data,
        ];
        // state.documentList[action.payload.jobId].total += 1;
      })
      .addCase(addNewDocumentData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(addDocumentTypeThunk.pending, (state) => {
        state.documentTypeLoading = true;
        state.error = null;
      })
      .addCase(addDocumentTypeThunk.fulfilled, (state, action) => {
        state.documentTypeLoading = false;
        state.documentTypeList = [action.payload, ...state.documentTypeList];
      })
      .addCase(addDocumentTypeThunk.rejected, (state, action) => {
        state.documentTypeLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateDocumentTypeThunk.pending, (state) => {
        state.documentTypeLoading = true;
        state.error = null;
      })
      .addCase(updateDocumentTypeThunk.fulfilled, (state, action) => {
        state.documentTypeLoading = false;
        state.documentTypeList = state.documentTypeList.map((dt) =>
          dt.documentTypeId === action.payload.id
            ? { ...dt, ...action.payload.data }
            : dt
        );
      })
      .addCase(updateDocumentTypeThunk.rejected, (state, action) => {
        state.documentTypeLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteDocumentTypeThunk.pending, (state) => {
        state.documentTypeLoading = true;
        state.error = null;
      })
      .addCase(deleteDocumentTypeThunk.fulfilled, (state, action) => {
        state.documentTypeLoading = false;
        state.documentTypeList = state.documentTypeList.filter(
          (dt) => dt.documentTypeId !== action.payload.id
        );
      })
      .addCase(deleteDocumentTypeThunk.rejected, (state, action) => {
        state.documentTypeLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default documentListSlice.reducer;
